import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import {
  MONTH_ORDER,
  calculateCurrentFiscalYear,
  type PerformanceRange,
  type BudgetVersion,
  type DepartmentPlRow,
  type BranchPlRow,
  type TrialBalanceRow,
} from '../components/profitAndLoss/constants';
import { db } from '../firebase/firebase';
import { useAuth } from '../firebase/AuthProvider';
import type { ProfitAndLossFilterValues, BranchDeptLogic } from '../components/ProfitAndLossSearchFilters';
import { ep } from '../api/endpoints';

// 保存済み検索条件を格納する Firestore コレクション
const SAVED_FILTERS_COLLECTION = 'HozenallCloud_Pl_SavedFilters';

// Firestore に保存された検索条件 1 件
export type SavedPlFilter = {
  id: string;
  name: string;
  filters: ProfitAndLossFilterValues;
  created_at?: any;
  // 表示順（D&D で並べ替えると採番される。未採番のものは created_at 降順で後ろに並ぶ）
  sort_order?: number;
};

/**
 * 損益ページ（経営概況 / 営業）が共有するデータ層フック。
 *
 * 試算表マトリクス・予算バージョン・部署別損益の取得と、絞り込み state（年度・予算・拠点・部署）、
 * 業績範囲・表示月の切替、ツリービュー（科目区分・勘定科目）の開閉までを一括で提供する。
 * 各ページはこのフックを呼び出し、返り値を表示セクションへ渡すだけで同一の損益ビューを構成できる。
 */
export function useProfitAndLoss() {
  const [rows, setRows] = useState<TrialBalanceRow[]>([]);
  // 初回マウント直後の最初の描画からローディングを表示し、プレースホルダのチラつきを防ぐ
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fiscalYear, setFiscalYear] = useState(calculateCurrentFiscalYear());
  const [budgetVersions, setBudgetVersions] = useState<BudgetVersion[]>([]);
  const [selectedBudgetVersion, setSelectedBudgetVersion] = useState<string>('');
  // 予算バージョンの初期取得が完了したか（成否問わず）。完了するまで試算表・部署別損益の取得を待ち、二重フェッチを防ぐ
  const [budgetReady, setBudgetReady] = useState(false);
  // 拠点絞り込み (branch_code 配列。空 = 全拠点)
  const [selectedBranchCodes, setSelectedBranchCodes] = useState<string[]>([]);
  // 部署絞り込み (department_code 配列。空 = 全部署)
  const [selectedDepartmentCodes, setSelectedDepartmentCodes] = useState<string[]>([]);
  // 拠点×部署の結合ロジック (and=かつ / or=または)。両方を選択したときのみ意味を持つ
  const [branchDeptLogic, setBranchDeptLogic] = useState<BranchDeptLogic>('and');
  // 業績範囲 (単月 / 四半期 / 半期 / 通期)。損益テーブル・上段サマリー・部署別ダッシュボードで共有し、同時に切り替わる
  const [performanceRange, setPerformanceRange] = useState<PerformanceRange>('month');
  // 表示対象の月（初期値は現在のカレンダー月）
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1); // 1-indexed
  // 適用中の保存条件（保存条件から適用したときのみ id/名前を保持。手動適用・クリアで null）。
  // ローディング中は rows が一旦空になりヘッダーが再マウントされるため、状態はフック側で保持する
  const [appliedFilterName, setAppliedFilterName] = useState<string | null>(null);
  // 適用中の保存条件ドキュメント id（保存済み条件セレクトの選択表示に使う）
  const [appliedFilterId, setAppliedFilterId] = useState<string | null>(null);

  // 🔖 保存済み検索条件（自分のもののみ）。onSnapshot でリアルタイム購読し、
  // 保存・削除は自動反映される。モーダルを開くたびの再取得は不要。
  const { user } = useAuth();
  const [savedFilters, setSavedFilters] = useState<SavedPlFilter[]>([]);
  useEffect(() => {
    if (!user?.email) return;
    const q = query(
      collection(db, SAVED_FILTERS_COLLECTION),
      where('user_id', '==', user.email),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as SavedPlFilter[];
        // 並び順はクライアント側でソートし Firestore index を不要にする。
        // sort_order(採番済み)を昇順で優先し、未採番は created_at 降順で後ろに並べる。
        list.sort((a, b) => {
          const sa = a.sort_order ?? null;
          const sb = b.sort_order ?? null;
          if (sa !== null && sb !== null) return sa - sb;
          if (sa !== null) return -1;
          if (sb !== null) return 1;
          const da = a.created_at?.toDate?.() ?? new Date(0);
          const dbb = b.created_at?.toDate?.() ?? new Date(0);
          return dbb.getTime() - da.getTime();
        });
        setSavedFilters(list);
      },
      (err) => console.error('保存済み条件の購読に失敗:', err),
    );
    return () => unsubscribe();
  }, [user?.email]);

  // 🔽 部署グループ別 通期損益 (損益本体とは独立して取得)。拠点別は同じ API の branch_data で併せて受け取る
  const [deptPlRows, setDeptPlRows] = useState<DepartmentPlRow[]>([]);
  const [branchPlRows, setBranchPlRows] = useState<BranchPlRow[]>([]);
  const [deptPlLoading, setDeptPlLoading] = useState(false);

  // 🔽 ツリービュー（科目区分セクションの開閉）の管理。初期状態は全セクション閉じる
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  // 🔽 ツリービュー（勘定科目の開閉）の管理
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const toggleAccount = (accountCode: string) => {
    setExpandedAccounts(prev => ({ ...prev, [accountCode]: !prev[accountCode] }));
  };

  /**
   * 🚀 時系列マトリクスデータを取得
   */
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setRows([]);

    try {
      const response = await axios.post(
        ep('get_obc_trial_balance'),
        {
          year: fiscalYear,
          mode: 'matrix', // 常にマトリクス形式を取得
          budget_version_code: selectedBudgetVersion || null,
          branch_codes: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
          department_codes: selectedDepartmentCodes.length > 0 ? selectedDepartmentCodes : null,
          branch_dept_logic: branchDeptLogic,
        }
      );

      if (response.data.status === 'error') {
        throw new Error(response.data.message || 'API側でエラーが発生しました');
      }

      const extractedData: TrialBalanceRow[] = response.data.data || [];

      if (extractedData.length === 0) {
        setError(`${fiscalYear}年度のデータがDBに見つかりませんでした。同期状況を確認してください。`);
      } else {
        setRows(extractedData);
      }
    } catch (err: any) {
      console.error(err);
      setError(`通信エラー: ${err.message || '不明なエラー'}`);
    } finally {
      setLoading(false);
    }
  };

  // 予算バージョン一覧の取得（初回のみ）
  useEffect(() => {
    const fetchBudgetVersions = async () => {
      try {
        const response = await axios.post(ep('get_active_budget_versions'), {});
        if (response.data.status === 'success') {
          const versions: BudgetVersion[] = response.data.data || [];
          setBudgetVersions(versions);
          if (versions.length > 0) {
            setSelectedBudgetVersion(versions[0].version_code);
          }
        }
      } catch (err) {
        console.error('予算バージョン取得エラー:', err);
      } finally {
        // 成否に関わらず初期取得は完了。これを契機に試算表・部署別損益を1回だけ取得する
        setBudgetReady(true);
      }
    };
    fetchBudgetVersions();
  }, []);

  // 試算表データの取得（年度・予算バージョン・拠点・部署変更時）。
  // 予算バージョンの初期取得が完了するまでは待ち、selectedBudgetVersion 確定後に1回だけ走らせる
  useEffect(() => {
    if (!budgetReady) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetReady, fiscalYear, selectedBudgetVersion, selectedBranchCodes, selectedDepartmentCodes, branchDeptLogic]);

  // 部署グループ別 通期損益の取得（年度・予算バージョン・拠点変更時）。損益本体とは独立し、失敗してもページは壊さない。
  // 損益本体と同様に予算バージョンの初期取得完了を待ち、二重フェッチを防ぐ
  useEffect(() => {
    if (!budgetReady) return;
    const fetchDeptPl = async () => {
      setDeptPlLoading(true);
      try {
        const response = await axios.post(
          ep('get_obc_pl_by_department'),
          {
            year: fiscalYear,
            budget_version_code: selectedBudgetVersion || null,
            branch_codes: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
            department_codes: selectedDepartmentCodes.length > 0 ? selectedDepartmentCodes : null,
            branch_dept_logic: branchDeptLogic,
          }
        );
        if (response.data.status === 'success') {
          setDeptPlRows(response.data.data || []);
          setBranchPlRows(response.data.branch_data || []);
        } else {
          setDeptPlRows([]);
          setBranchPlRows([]);
        }
      } catch (err) {
        console.error('部署別損益の取得エラー:', err);
        setDeptPlRows([]);
        setBranchPlRows([]);
      } finally {
        setDeptPlLoading(false);
      }
    };
    fetchDeptPl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetReady, fiscalYear, selectedBudgetVersion, selectedBranchCodes, selectedDepartmentCodes, branchDeptLogic]);

  /**
   * 集計用 rows の重複除去
   *
   * 予算データ (Excel フォーマット由来) は同 account_code に対して
   * 「科」行 (sub_account_code 空, 科目合計) と 「補」行 (sub_account_code 有, 内訳) を同額で両方保持する。
   * 単純合算すると二重カウントになるため、「補」行が存在する account_code に限り「科」行を除外する。
   *
   * 判定材料は「予算側に補行があるか」に限定する。実績側 (OBC API 由来) は補助科目を
   * 持たない科目に対しても sub_account_code='0' (=「その他」) を返してくる仕様のため、
   * 実績の sub 有無を判定材料に含めると補なし運用科目 (本社負担金等) の予算「科」行が
   * 誤って除外される。「その他」が実体ある補助科目として並列運用される将来運用にも
   * このロジックで対応可能。
   */
  const dedupedRows = useMemo(() => {
    const accHasBudgetSub = new Set<string>();
    rows.forEach(r => {
      if (!r.sub_account_code) return;
      const anyBudget = MONTH_ORDER.some(m => (Number(r[`month_${m}_budget`]) || 0) !== 0);
      if (anyBudget && r.account_code) accHasBudgetSub.add(r.account_code);
    });
    return rows.filter(r => {
      // 予算「科」行 (sub 空) で、同 account_code に予算「補」行が存在する場合のみ除外
      if (!r.sub_account_code && r.account_code && accHasBudgetSub.has(r.account_code)) {
        return false;
      }
      return true;
    });
  }, [rows]);

  return {
    // 損益本体
    rows,
    dedupedRows,
    loading,
    error,
    // 絞り込み state
    fiscalYear,
    setFiscalYear,
    budgetVersions,
    selectedBudgetVersion,
    setSelectedBudgetVersion,
    selectedBranchCodes,
    setSelectedBranchCodes,
    selectedDepartmentCodes,
    setSelectedDepartmentCodes,
    branchDeptLogic,
    setBranchDeptLogic,
    // 適用中の保存条件
    appliedFilterName,
    setAppliedFilterName,
    appliedFilterId,
    setAppliedFilterId,
    // 保存済み条件（リアルタイム購読）
    savedFilters,
    // 業績範囲・表示月
    performanceRange,
    setPerformanceRange,
    currentMonth,
    setCurrentMonth,
    // ツリービュー開閉
    expandedSections,
    toggleSection,
    expandedAccounts,
    toggleAccount,
    // 部署別・拠点別損益
    deptPlRows,
    branchPlRows,
    deptPlLoading,
  };
}

export type UseProfitAndLossResult = ReturnType<typeof useProfitAndLoss>;
